import React from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * PLAT-11059 DS6 — the in-flight state a naming run puts on the row.
 *
 * One component for both headings (a Section row and a Chapter heading) so the
 * two cannot drift. In the accent colour, which is the design system's colour for
 * generative work, and with dots that move: the row is waiting on a round trip,
 * and static text in the name's slot reads as a name that says
 * "Generating a name…".
 *
 * Announcement stays with the host — a Chapter's heading already marks the line
 * it shares with the section count as a status region, and a second `role` here
 * would announce one run twice.
 */
export const GeneratingNameLabel: React.FC<{
  /**
   * What is being generated, when it is not a name — the Auto-Chapter dialog
   * runs the same "the AI is working on it" shape over a whole reel. The moving
   * dots and the accent colour are what this component is; the noun is the
   * caller's.
   */
  label?: string;
}> = ({ label = 'Generating a name' }) => (
  <Label>
    {label}
    {/* Decorative: the phrase already says what is happening, and three
        separately-announced full stops say nothing. */}
    <Dots aria-hidden="true">
      <span>.</span>
      <span>.</span>
      <span>.</span>
    </Dots>
  </Label>
);

const travel = keyframes`
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
`;

const Label = styled.span`
  color: var(--accent);
  font-style: italic;
`;

// Staggered rather than simultaneous — three dots fading in unison is a pulse,
// and the point is left-to-right movement.
const Dots = styled.span`
  span {
    animation: ${travel} 1.4s ease-in-out infinite;
  }
  span:nth-child(2) {
    animation-delay: 0.2s;
  }
  span:nth-child(3) {
    animation-delay: 0.4s;
  }
`;
